import { useTranslation } from 'react-i18next';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Badge, Box, Button, Drawer, Header, Typography } from '@robocorp/components';
import { IconFloppyDisc, IconPlus } from '@robocorp/icons/iconic';
import { ChangeEvent, FC, ReactNode, useCallback, useEffect, useMemo, useRef } from 'react';
import { JSONEditor, ViewLoader } from '~/components';
import {
  useDeleteWorkItemFileMutation,
  useUpdateWorkItemDataMutation,
  useUploadWorkItemFileMutation,
  useWorkItemQuery,
} from '~/queries';
import { WorkItemFileList } from '~/routes/workspace/processes/views/workItem/form/file';
import {
  formatPayload,
  WorkItemDetailForm,
  workItemDetailSchema,
} from '~/routes/workspace/processes/views/workItem/form/schema';
import { WorkItemState } from '~/types/resources';
import { WorkItemActions } from './Actions';

/**
 * Removes thrown exception message from exception code.
 * Full message can be viewed on work item page
 */
const parseExceptionMessage = (message = '') => message.replace(/: {[^[\]]*\}/, '');

interface Props {
  workItemId: string;
  processId: string;
  children?: ReactNode;
  onClose: () => void;
}

export const WorkItemContent: FC<Props> = ({ workItemId, processId, children, onClose }) => {
  const { t } = useTranslation();

  const fileInput = useRef<HTMLInputElement | null>(null);
  const fileUploadRecord = useRef<Record<string, File>>({});
  const fileRemoveList = useRef<string[]>([]);

  const { data: workItem, isLoading: workItemIsLoading } = useWorkItemQuery({
    processId,
    workItemId,
  });

  const { mutateAsync: updateWorkItemDataMutation } = useUpdateWorkItemDataMutation();
  const { mutateAsync: uploadWorkItemFileMutation } = useUploadWorkItemFileMutation();
  const { mutateAsync: deleteWorkItemFileMutation } = useDeleteWorkItemFileMutation();

  const onWorkItemDataUpdate = (payload: string | null | undefined) =>
    updateWorkItemDataMutation({
      workItemId,
      payload: typeof payload === 'string' ? JSON.parse(payload) : payload,
    });

  const defaultValues = useMemo(
    () => ({
      payload: formatPayload(workItem?.payload),
      files: workItem?.files ?? [],
    }),
    [workItem?.payload, workItem?.files],
  );

  const {
    control,
    register,
    handleSubmit,
    getValues,
    reset,
    formState: { isSubmitting, isSubmitSuccessful, isDirty },
  } = useForm<WorkItemDetailForm>({
    resolver: zodResolver(workItemDetailSchema),
    defaultValues,
  });

  register('files');
  const {
    fields: fileFields,
    prepend,
    remove,
  } = useFieldArray({ control, name: 'files', keyName: 'formFieldKey' });

  useEffect(() => {
    if (isSubmitSuccessful) {
      reset(getValues());
    }
  }, [isSubmitSuccessful, reset, getValues]);

  /**
   * Sync form data if user hasn't changed it manually
   */
  useEffect(() => {
    if (!isDirty) {
      reset(defaultValues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultValues]);

  const onFileAdd = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const fileList = Array.from(event.target.files ?? []);
      fileList.forEach((file) => {
        fileUploadRecord.current[file.name] = file;
        prepend({ name: file.name, size: file.size });
      });
    },
    [prepend],
  );

  const onFileRemove = useCallback(
    (index: number) => {
      const file = getValues(`files.${index}`);
      if (file?.id) {
        fileRemoveList.current.push(file.id);
      }
      if (file?.name) {
        delete fileUploadRecord.current?.[file.name];
      }
      remove(index);
    },
    [getValues, remove],
  );

  const fields = (fileFields ?? []).map((item, index) => ({
    ...item,
    index,
  }));

  const onSubmit = async ({ payload }: WorkItemDetailForm) => {
    const bothEmptyPayloads = !payload && !defaultValues.payload;
    if (!bothEmptyPayloads && payload !== defaultValues.payload) {
      await onWorkItemDataUpdate(payload);
    }

    const fileUploadList = Object.values(fileUploadRecord.current) ?? [];
    // eslint-disable-next-line no-restricted-syntax
    for (const file of fileUploadList) {
      // eslint-disable-next-line no-await-in-loop
      await uploadWorkItemFileMutation({ workItemId, file });
    }
    fileUploadRecord.current = {};

    // eslint-disable-next-line no-restricted-syntax
    for (const fileId of fileRemoveList.current ?? []) {
      // eslint-disable-next-line no-await-in-loop
      await deleteWorkItemFileMutation({ workItemId, fileId });
    }
    fileRemoveList.current = [];
  };

  const actionsDisabled = !workItem;

  const message = workItem?.exception?.message;
  const exceptionMessage = useMemo(() => {
    if (!message) {
      return undefined;
    }
    return parseExceptionMessage(message);
  }, [message]);

  const addFileAllowed = useMemo(
    () =>
      workItem?.state !== undefined &&
      [WorkItemState.New, WorkItemState.Failed].includes(workItem.state),
    [workItem?.state],
  );

  return (
    <>
      <Drawer.Content>
        {workItemIsLoading ? (
          <ViewLoader />
        ) : (
          <>
            {workItem?.exception && (
              <Header size="small">
                <Header.Title
                  title={
                    <Typography
                      as="span"
                      color={exceptionMessage ? 'content.error' : undefined}
                      fontWeight={500}
                    >
                      {exceptionMessage || t('noMessage')}
                    </Typography>
                  }
                />
                <Header.Description>
                  {t(`workItemExceptionType.${workItem.exception.type}`)}
                </Header.Description>
              </Header>
            )}

            <Box pt="$8" mb="$16">
              <Controller
                control={control}
                name="payload"
                render={({ field: { value, onChange }, fieldState: { error } }) => (
                  <JSONEditor
                    title={t('payload')}
                    value={value || '{ "":""}'}
                    onChange={onChange}
                    error={!!error}
                    setError={() => null}
                  />
                )}
              />
            </Box>

            <Header size="small">
              <Header.Title title={t('files.title')}>
                <Badge label={fields?.length} />
              </Header.Title>
              <Header.Actions>
                {addFileAllowed && (
                  <Button
                    onClick={() => fileInput.current?.click()}
                    variant="ghost"
                    icon={IconPlus}
                  >
                    {t('workItem.addFiles')}
                  </Button>
                )}
              </Header.Actions>
            </Header>

            <WorkItemFileList
              ref={fileInput}
              workItemId={workItemId}
              data={fields}
              newFileCount={Object.keys(fileUploadRecord.current).length}
              onRemove={onFileRemove}
              onChange={onFileAdd}
            />
            {children}
          </>
        )}
      </Drawer.Content>

      <WorkItemActions
        workItemId={workItemId}
        processId={processId}
        workItemState={workItem?.state}
        hasWorkItemPayload={!!workItem?.payload}
        disabled={actionsDisabled}
        onDelete={onClose}
      >
        <Button
          variant="secondary"
          onClick={handleSubmit(onSubmit)}
          disabled={actionsDisabled}
          loading={isSubmitting}
          icon={IconFloppyDisc}
        >
          {t('save')}
        </Button>
      </WorkItemActions>
    </>
  );
};
